import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Fade,
} from "@mui/material";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import DeliveryDiningIcon from "@mui/icons-material/DeliveryDining";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import { getStatusColor } from "./constants";
import { useState } from "react";

const StatusBanner = ({ orderId, currentStatus, deliveryTime,invoice_number }) => {
  const [copied, setCopied] = useState(false);

  const handleCopyOrderId = () => {
    navigator.clipboard.writeText(orderId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getIcon = (size = 24) => {
    const iconStyle = { fontSize: size };
    switch (currentStatus) {
      case "Packing":
        return <Inventory2Icon sx={iconStyle} />;
      case "In Progress":
        return <PlayArrowIcon sx={iconStyle} />;
      case "In Transit":
        return <LocalShippingIcon sx={iconStyle} />;
      case "Ready for Delivery":
        return <DeliveryDiningIcon sx={iconStyle} />;
      case "Completed":
      case "Delivered":
        return <CheckCircleIcon sx={iconStyle} />;
      case "Canceled":
        return <CancelIcon sx={iconStyle} />;
      case "Paused":
        return <PauseCircleIcon sx={iconStyle} />;
      default:
        return <LocalShippingIcon sx={iconStyle} />;
    }
  };

  const statusColor = getStatusColor(currentStatus);

  return (
    <Fade in={true} timeout={800}>
      <Card
        elevation={0}
        sx={{
          mb: 3,
          background: `linear-gradient(135deg, ${statusColor} 0%, ${statusColor}dd 100%)`,
          color: "white",
          borderRadius: "16px",
          border: `2px solid ${statusColor}`,
          position: "relative",
          overflow: "hidden",
          transition: "all 0.3s ease",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: `0 8px 24px ${statusColor}66`,
          },
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            right: 0,
            width: "200px",
            height: "200px",
            background: "rgba(255, 255, 255, 0.1)",
            borderRadius: "50%",
            transform: "translate(50%, -50%)",
          },
        }}
      >
        <CardContent
          sx={{ p: { xs: 2.5, md: 3.5 }, position: "relative", zIndex: 1 }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              flexDirection: { xs: "column", md: "row" },
              gap: 2,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <Typography
                  variant="h5"
                  fontWeight="800"
                  sx={{
                    textShadow: "0 2px 4px rgba(0,0,0,0.2)",
                  }}
                >
                  {/* Invoice/Job No. {orderId || "N/A"} */}
                  Invoice No. {invoice_number || "N/A"}
                </Typography>
                <Tooltip title={copied ? "Copied!" : "Copy Order No."}>
                  <IconButton
                    size="small"
                    onClick={handleCopyOrderId}
                    sx={{
                      color: "white",
                      backgroundColor: "rgba(255,255,255,0.2)",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.3)",
                      },
                      transition: "all 0.2s",
                    }}
                  >
                    {copied ? (
                      <CheckIcon fontSize="small" />
                    ) : (
                      <ContentCopyIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 64,
                    height: 64,
                    borderRadius: "50%",
                    backgroundColor: "rgba(255,255,255,0.25)",
                    backdropFilter: "blur(10px)",
                    border: "3px solid rgba(255,255,255,0.4)",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    animation:
                      currentStatus === "In Transit" ||
                      currentStatus === "In Progress"
                        ? "pulse 2s ease-in-out infinite"
                        : "none",
                    "@keyframes pulse": {
                      "0%, 100%": {
                        transform: "scale(1)",
                        opacity: 1,
                      },
                      "50%": {
                        transform: "scale(1.05)",
                        opacity: 0.9,
                      },
                    },
                  }}
                >
                  {getIcon(32)}
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    display="block"
                    sx={{
                      opacity: 0.9,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: 1,
                      mb: 0.5,
                    }}
                  >
                    Current Status
                  </Typography>
                  <Chip
                    label={currentStatus || "Unknown"}
                    sx={{
                      backgroundColor: "rgba(255,255,255,0.95)",
                      color: statusColor,
                      fontWeight: "bold",
                      fontSize: "0.9rem",
                      px: 1,
                      height: 32,
                      "& .MuiChip-label": {
                        px: 2,
                      },
                    }}
                  />
                </Box>
              </Box>

              {deliveryTime && (
                <Typography
                  variant="body2"
                  sx={{
                    mt: 2,
                    opacity: 0.95,
                    fontWeight: 500,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  Delivery Time: <strong>{deliveryTime}</strong>
                </Typography>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Fade>
  );
};

export default StatusBanner;
